import {
  Container,
  SearchBodyContainer
} from 'components/Container/Container.css';
import { useEffect, useMemo, useState } from 'react';
import {
  Configure,
  Hits,
  Pagination,
  useInstantSearch,
  usePagination,
  useSearchBox
} from 'react-instantsearch';
import { isMobile as getIsMobile } from 'utils/misc';
import { ParamsResult } from 'ParamsResult';
import { ALGOLIA_INDEX_NAME, AlgoliaFilterAttributes } from 'constants/algolia';
import { userTokenAlgolia } from 'utils/algolia';
import { TabsNavigation } from 'components/TabsNavigation';
import { defaultSearchTitle, tabsSearch } from 'constants/search';
import { TabItem } from 'components/TabsNavigation/TabsNavigation';
import { Location } from 'location';
import { buildBreadcrumbList } from 'components/ClosedSearchSubHeader/ClosedSearchSubHeader';
import getSearchSpecificTerms from 'components/SearchSpecificTerms/getSearchSpecificTerms';
import EmptySearch from 'components/EmptySearch/EmptySearch';
import { Loading } from 'components/Loading';
import {
  Breadcrumbs,
  Button,
  IconArrowLeft,
  IconArrowRight
} from '@sympla/sympla-components';
import { ANYWHERE_LOCATION, NEAR_TO_YOU_LABEL } from 'constants/index';
import { listPredefinedDates } from 'constants/listPredefinedDates';
import {
  FilterItemsWrapper,
  FiltersContainer,
  FiltersTitle,
  FiltersWrapper,
  TabsWrapper,
  HitsList,
  HitsItem,
  PaginationList,
  PaginationListItem,
  PaginationListItemSelected,
  Subtitle,
  Title,
  TotalPages,
  PaginationWrapper,
  PaginationArrow,
  PaginationArrowContainer,
  ContainerHeaderPredefinedSearch,
  BreadcrumbsPadding,
  TitlePredefinedSearch
} from './AlgoliaSearchEvents.css';
import { DropdownFilter } from 'components/DropdownFilter/DropdownFilter';
import { DateFilter } from 'components/DateFilter/DateFilter';
import { SortByFilter } from 'components/SortByFilter/SortByFilter';
import { AlgoliaSearchHit } from 'components/AlgoliaSearchHit/AlgoliaSearchHit';
import SearchOtherCities from 'components/SearchOtherCities/SearchOtherCities';
import { useAlgoliaDateFilters } from 'hooks/useAlgoliaDateFilters';

interface AlgoliaSearchEventsProps {
  params: ParamsResult;
  handleChangeTabs?: (tabName: TabItem) => void;
  location: Location;
  termURL?: string;
  date?: string;
  title?: string;
}

export function AlgoliaSearchEvents({
  params,
  handleChangeTabs,
  location,
  termURL = '',
  date = '',
  title = ''
}: AlgoliaSearchEventsProps) {
  const isMobile = getIsMobile();
  const ComponentSpecificTerms = getSearchSpecificTerms(termURL);
  const { query, refine } = useSearchBox();
  const { results } = useInstantSearch();
  const { refine: refinePage, currentRefinement: currentPage } =
    usePagination();

  const [dateFilter, setDateFilter] = useState<string | null>(date);

  const { numericFilters, formattedDate } = useAlgoliaDateFilters(dateFilter);

  const applyCityFilter =
    location.city !== ANYWHERE_LOCATION && location.city !== NEAR_TO_YOU_LABEL;

  const handlePageChange = (type: 'prev' | 'next') => {
    if (type === 'prev') {
      refinePage(currentPage - 1);
    } else {
      refinePage(currentPage + 1);
    }
    window?.scrollTo({ top: 0, left: 0 });
  };

  const getTitle = useMemo(() => {
    if (params?.search_params?.q) {
      return (
        <h1 className={Title}>
          &quot;
          {params?.search_params?.q}
          &quot;
        </h1>
      );
    }
    let newTitle = title;
    if (date !== dateFilter) {
      const searchEmpty = listPredefinedDates.find(
        (item) => item?.param === dateFilter
      );
      newTitle = searchEmpty?.label
        ? `Eventos ${searchEmpty?.label}`
        : defaultSearchTitle;
    }
    return <h1 className={TitlePredefinedSearch}>{newTitle}</h1>;
  }, [title, date, dateFilter, params?.search_params?.q]);

  useEffect(() => {
    refine(params?.search_params?.q || '');
  }, [params, date]);

  return (
    <div>
      <Configure
        hitsPerPage={24}
        analytics
        numericFilters={numericFilters}
        facetFilters={applyCityFilter ? [`city:${location.city}`] : undefined}
        userToken={userTokenAlgolia}
      />
      <div className={Container} style={{ paddingBottom: 0 }}>
        <div className={BreadcrumbsPadding}>
          <Breadcrumbs list={buildBreadcrumbList(params)} />
        </div>
        <div className={`${query ? '' : ContainerHeaderPredefinedSearch}`}>
          {getTitle}
          <div className={FiltersContainer}>
            {query && (
              <div className={TabsWrapper}>
                <TabsNavigation
                  tabsList={[
                    {
                      label: tabsSearch.event.label,
                      tabName: tabsSearch.event.tabName,
                      ariaLabel: tabsSearch.event.ariaLabel
                    },
                    {
                      label: tabsSearch.place.label,
                      tabName: tabsSearch.place.tabName,
                      ariaLabel: tabsSearch.place.ariaLabel
                    },
                    {
                      label: tabsSearch.organizer.label,
                      tabName: tabsSearch.organizer.tabName,
                      ariaLabel: tabsSearch.organizer.ariaLabel
                    }
                  ]}
                  handleChange={handleChangeTabs}
                />
              </div>
            )}
            <div className={FiltersWrapper}>
              {!isMobile && <p className={FiltersTitle}>Filtrar por</p>}
              <div className={FilterItemsWrapper}>
                <DropdownFilter attribute="theme" attributeLabel="Categoria" />
                <DateFilter
                  value={formattedDate}
                  onDateSelect={setDateFilter}
                  label="Data"
                />
                <DropdownFilter
                  attribute={AlgoliaFilterAttributes.IS_ACCESSIBLE_FOR_FREE}
                  attributeLabel="Preço"
                />
                <DropdownFilter
                  attribute={AlgoliaFilterAttributes.TYPE}
                  attributeLabel="Tipo Evento"
                />
                {isMobile && (
                  <SortByFilter
                    title="Ordenar por"
                    items={[
                      { value: ALGOLIA_INDEX_NAME || '', label: 'Relevância' },
                      { value: `${ALGOLIA_INDEX_NAME}_date_asc`, label: 'Data' }
                    ]}
                  />
                )}
              </div>
            </div>
            {!isMobile && (
              <div className={FiltersWrapper}>
                <p className={FiltersTitle}>Ordenar por</p>
                <div className={FilterItemsWrapper}>
                  <SortByFilter
                    title="Ordenar por"
                    items={[
                      { value: ALGOLIA_INDEX_NAME || '', label: 'Relevância' },
                      { value: `${ALGOLIA_INDEX_NAME}_date_asc`, label: 'Data' }
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!results?.queryID ? (
        <Loading isNotFullScreen />
      ) : (
        <div className={SearchBodyContainer} style={{ padding: '40px 0' }}>
          <div className={Container}>
            <div className={Subtitle}>
              {results?.nbHits > 1 ? (
                <span>{`${results?.nbHits} eventos encontrados`}</span>
              ) : (
                <span>{`${results?.nbHits} evento encontrado`}</span>
              )}
            </div>
            {ComponentSpecificTerms}
            {results?.nbHits === 0 &&
              (applyCityFilter ? (
                <SearchOtherCities
                  termSearch={termURL}
                  userTokenAlgolia={userTokenAlgolia}
                />
              ) : (
                <EmptySearch />
              ))}
            {results?.nbHits > 0 && (
              <>
                <div className={TotalPages}>{`Página ${results.page + 1} de ${
                  results?.nbPages
                }`}</div>
                <Hits
                  classNames={{ list: HitsList, item: HitsItem }}
                  hitComponent={AlgoliaSearchHit}
                />
                {results?.nbPages > 1 && (
                  <div className={PaginationWrapper}>
                    {results?.page > 0 && (
                      <Button
                        variant="text"
                        rounded
                        upperCase={false}
                        onClick={() => handlePageChange('prev')}
                      >
                        <div className={PaginationArrowContainer}>
                          <IconArrowLeft className={PaginationArrow} />
                          Anterior
                        </div>
                      </Button>
                    )}
                    <Pagination
                      classNames={{
                        list: PaginationList,
                        item: PaginationListItem,
                        selectedItem: PaginationListItemSelected
                      }}
                      showPrevious={false}
                      showFirst={false}
                      showNext={false}
                      showLast={false}
                    />
                    {results?.page < results.nbPages - 1 && (
                      <Button
                        variant="text"
                        rounded
                        upperCase={false}
                        onClick={() => handlePageChange('next')}
                      >
                        <div className={PaginationArrowContainer}>
                          Próximo
                          <IconArrowRight className={PaginationArrow} />
                        </div>
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
